import { useEffect, useMemo, useState } from "react"
import { UseFormReturn, useWatch } from "react-hook-form"
import { CampingStyleProps, PriceProps, SiteProps } from "@/interfaces"
import { bySort } from "@/utils/sorts"
import { DURATION_ORDER, ELECTRICS_ORDER } from "@/components/Admin/Settings/pages/pricing/constants"
import { ELECTRICS } from "~/constants"
import { AvailableConfigs, FormValues, PriceConfig, PriceEntry } from "~/components/Admin/Settings/pages/pricing/types"
import { validDurations, validElectric } from "~/types"
import { useDebouncedCallback } from "use-debounce"
import api from "~/api2"
import { PRICE_DURATIONS } from "~/constants/price_durations"
import { getGroupKeys } from "~/components/Admin/Settings/pages/pricing/utils"
import _ from "lodash"

interface UsePricingTableProps {
  campingStyle: CampingStyleProps | undefined
  reactHookForm: UseFormReturn<FormValues>
  allSites: SiteProps[]
  showNotification: (options: any) => void
}

const usePricingTable = ({
  campingStyle,
  reactHookForm,
  allSites,
  showNotification,
}: UsePricingTableProps) => {
  const { setValue } = reactHookForm

  const [prices, setPrices] = useState<PriceProps[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)

  // Use useWatch to observe priceConfig changes
  const priceConfig =
    useWatch({
      control: reactHookForm.control,
      name: "priceConfig",
    }) || {}

  // Fetch prices when campingStyle.id changes
  useEffect(() => {
    setIsLoading(true)
    debouncedFetchPrices()
  }, [_.isEmpty(priceConfig)])

  // Fetch prices when campingStyle.id changes
  useEffect(() => {
    setIsLoading(true)
    debouncedFetchPrices()
  }, [campingStyle?.id])

  const debouncedFetchPrices = useDebouncedCallback(() => {
    if (!campingStyle || !campingStyle.id) return
    api.CampingStyles.getPricesFor(campingStyle?.id)
      .then(({ prices: persistedPrices }) => {
        // Initialize priceConfig in react-hook-form
        const initialPriceConfig: PriceConfig = {}

        persistedPrices.forEach((price) => {
          const { duration, electric, amount_in_cents, site_id } = price
          const electricKey = electric || ELECTRICS.NONE
          const key = `${duration}_${electricKey}_${amount_in_cents}`

          if (!initialPriceConfig[key]) {
            initialPriceConfig[key] = []
          }
          initialPriceConfig[key].push({ siteId: site_id, id: price.id, _destroy: false })
        })

        // Set initial priceConfig into react-hook-form
        setValue("priceConfig", initialPriceConfig)

        setPrices(persistedPrices)
      })
      .catch((err) => {
        showNotification({
          type: "error",
          title: `Could not load ${campingStyle?.name} prices`,
          description: err.message,
        })
      })
      .finally(() => setIsLoading(false))
  }, 600)

  const sites = useMemo<SiteProps[]>(() => {
    return allSites.filter((site) => site.camping_style_id === campingStyle?.id).sort(bySort)
  }, [allSites, campingStyle?.id])

  // Compute sitePriceCounts whenever priceConfig changes
  const sitePriceCounts = useMemo(() => {
    const counts: { [siteId: string]: number } = {}
    // Initialize counts to 0
    sites.forEach((site) => {
      const siteId = site.id.toString()
      counts[siteId] = 0
    })

    // Count the number of price configurations per site
    Object.values(priceConfig).forEach((priceConfig) => {
      priceConfig.forEach(({ siteId, _destroy }) => {
        if (counts[siteId] !== undefined && !_destroy) {
          counts[siteId] += 1
        }
      })
    })
    return counts
  }, [priceConfig, sites])

  // Create functions to enable and disable prices
  const enablePrice = (key: string, siteId: string) => {
    const currentConfig: PriceConfig = priceConfig || {}

    const newConfig: PriceConfig = Object.keys(currentConfig).reduce((acc, configKey) => {
      acc[configKey] = currentConfig[configKey].map(price => ({ ...price }))
      return acc
    }, {} as PriceConfig)

    const isFlatRateKey = key.includes(PRICE_DURATIONS.FLAT_RATE)

    if (isFlatRateKey) {
      Object.keys(newConfig).forEach((configKey) => {
        if (configKey !== key) {
          newConfig[configKey] = newConfig[configKey].map((price) =>
            price.siteId === siteId ? { ...price, _destroy: true } : price
          )
        }
      })
    } else {
      Object.keys(newConfig).forEach((configKey) => {
        if (configKey.includes(PRICE_DURATIONS.FLAT_RATE)) {
          newConfig[configKey] = newConfig[configKey].map((price) =>
            price.siteId === siteId ? { ...price, _destroy: true } : price
          )
        }
      })
    }

    if (!newConfig[key]) {
      newConfig[key] = []
    }

    const existingPriceIndex = newConfig[key].findIndex((price) => price.siteId === siteId)

    if (existingPriceIndex !== -1) {
      if (newConfig[key][existingPriceIndex]._destroy) {
        newConfig[key][existingPriceIndex]._destroy = false
      }
    } else {
      newConfig[key].push({ siteId, _destroy: false })
    }

    setValue("priceConfig", newConfig)
  }

  const disablePrice = (key: string, siteId: string) => {
    const currentConfig: PriceConfig = priceConfig || {}

    const newConfig: PriceConfig = Object.keys(currentConfig).reduce((acc, configKey) => {
      acc[configKey] = currentConfig[configKey].map(price => ({ ...price }))
      return acc
    }, {} as PriceConfig)

    const sitePriceCount = sitePriceCounts[siteId]

    if (sitePriceCount > 1) {
      if (newConfig[key]) {
        const existingPrice = newConfig[key].find((config) => config.siteId === siteId)
        if (existingPrice) {
          existingPrice._destroy = true
        }
        setValue("priceConfig", newConfig)
      }
    }
  }

  // Implement select all and unselect all
  const selectAll = (key: string) => {
    const currentConfig: PriceConfig = priceConfig || {}
    const newConfig: PriceConfig = { ...currentConfig }
    const isFlatRateKey = key.includes(PRICE_DURATIONS.FLAT_RATE)
    const [duration, electric] = key.split("_")

    // Get all keys in the same group (same duration and electric)
    const groupKeys = getGroupKeys(currentConfig, duration as validDurations, electric as validElectric)

    // Iterate through each site to determine if selection is possible
    const pricesToSelect: PriceEntry[] = sites.reduce<PriceEntry[]>((acc, site) => {
      // Check if any other key in the group is already selected for this site
      const isOtherKeySelected = groupKeys.some((groupKey) => {
        if (groupKey === key) return false // Skip the current key
        return currentConfig[groupKey]?.some(
          (price) => price.siteId === site.id && !price._destroy
        )
      })

      // If no other key is selected in the group, select this key for the site
      if (!isOtherKeySelected) {
        const existingPrice = currentConfig[key]?.find((price) =>price.siteId === site.id)

        acc.push({
          siteId: site.id,
          id: existingPrice?.id,
          _destroy: false,
        })
      }

      return acc
    }, [])

    // Handle Flat Rate Logic
    if (isFlatRateKey) {
      // If selecting a flat rate key, remove all sites from other flat rate configurations
      Object.keys(newConfig).forEach((configKey) => {
        if (configKey !== key && configKey.includes(PRICE_DURATIONS.FLAT_RATE)) {
          newConfig[configKey] = newConfig[configKey].filter(
            ({ siteId }) => !pricesToSelect.some((price) => price.siteId === siteId)
          )
        }
      })
    } else {
      // If selecting a non-flat rate key, remove all sites from flat rate configurations
      Object.keys(newConfig).forEach((configKey) => {
        if (configKey.includes(PRICE_DURATIONS.FLAT_RATE)) {
          newConfig[configKey] = newConfig[configKey].filter(
            ({ siteId }) => !pricesToSelect.some((price) => price.siteId === siteId)
          )
        }
      })
    }

    // Add selected prices to the current key
    if (!newConfig[key]) {
      newConfig[key] = []
    }

    pricesToSelect.forEach((priceToSelect) => {
      const existingPriceIndex = newConfig[key].findIndex(
        (price) => price.siteId === priceToSelect.siteId
      )

      if (existingPriceIndex !== -1) {
        // Update existing price entry
        newConfig[key][existingPriceIndex] = {
          ...newConfig[key][existingPriceIndex],
          id: priceToSelect.id, // Update ID if available
          _destroy: false,
        }
      } else {
        // Add new price entry
        newConfig[key].push(priceToSelect)
      }
    })

    // Update the form state with the new configuration
    setValue("priceConfig", newConfig)
  }


  const unselectAll = (key: string) => {
    const currentConfig: PriceConfig = priceConfig || {}

    const newConfig: PriceConfig = Object.keys(currentConfig).reduce((acc, configKey) => {
      acc[configKey] = currentConfig[configKey].map(price => ({ ...price }))
      return acc
    }, {} as PriceConfig)

    const selectedPrices = newConfig[key] || []

    selectedPrices.forEach(price => {
      const { siteId } = price
      const totalPricesForSite = sitePriceCounts[siteId]

      if (totalPricesForSite > 1) {
        Object.keys(newConfig).forEach(configKey => {
          newConfig[configKey].forEach(priceEntry => {
            if (priceEntry.siteId === siteId && configKey === key) {
              priceEntry._destroy = true
            }
          })
        })
      }
    })

    setValue("priceConfig", newConfig)
  }

  // Update price configurations
  const updatePriceConfig = (
    oldKey: string | undefined,
    newPriceData: {
      amount_in_cents: number
      duration: validDurations
      electric: validElectric
    },
    forceUpdate: boolean = false,
    conflictKey?: string
  ) => {
    const currentConfig = priceConfig || {}
    const newConfig = { ...currentConfig }
    const newKey = `${newPriceData.duration}_${newPriceData.electric}_${newPriceData.amount_in_cents * 100}`
    if (oldKey && oldKey !== newKey) {
      if (currentConfig[newKey] && !forceUpdate) {
        return false
      }

      // Merge sites from old key and new key
      const oldSites = newConfig[oldKey] || []
      const newSites = newConfig[newKey] || []

      newConfig[newKey] = Array.from(new Set([...newSites, ...oldSites]))
      delete newConfig[oldKey]
      conflictKey && delete newConfig[conflictKey]
    } else if (!oldKey) {
      if (currentConfig[newKey]) {
        showNotification({
          type: "error",
          title: "Price configuration already exists",
          description: "A price configuration with the same duration, electric, and amount already exists.",
        })
        return false
      }
      newConfig[newKey] = []
    }

    setValue("priceConfig", newConfig)
    return true
  }

  const removePriceConfig = (key: string) => {
    const currentConfig = priceConfig || {}
    const newConfig = { ...currentConfig }

    if (newConfig[key]) {
      delete newConfig[key]
    }

    setValue("priceConfig", newConfig)
  }

  // Compute available configurations from prices
  const availableConfigs = useMemo<AvailableConfigs>(() => {
    const configs: AvailableConfigs = {}

    Object.keys(priceConfig).forEach((key) => {
      const [duration, electric, amount_in_cents_str] = key.split("_")
      const amount_in_cents = parseFloat(amount_in_cents_str)

      if (!configs[duration]) {
        configs[duration] = {}
      }

      const electricKey = electric || ELECTRICS.NONE

      if (!configs[duration][electricKey]) {
        configs[duration][electricKey] = {
          electric: electricKey,
          amounts: [], // Initialize as an empty array
        }
      }

      configs[duration][electricKey].amounts.push(amount_in_cents)
    })

    // Optionally sort amounts for each electric
    Object.values(configs).forEach((electricsObj) => {
      Object.values(electricsObj).forEach((electric) => {
        electric.amounts.sort((a, b) => a - b)
      })
    })

    return configs
  }, [priceConfig])


  // Prepare headers for the table
  const headers = useMemo(() => {
    const entries = Object.entries(availableConfigs)

    const sortedEntries = entries.sort((a, b) => {
      const durationA = a[0]
      const durationB = b[0]
      const indexA = DURATION_ORDER.indexOf(durationA)
      const indexB = DURATION_ORDER.indexOf(durationB)

      if (indexA === -1 && indexB === -1) return 0
      if (indexA === -1) return 1
      if (indexB === -1) return -1

      return indexA - indexB
    })

    return sortedEntries.map(([duration, electricsObj]) => ({
      duration,
      electrics: Object.values(electricsObj)
        .sort((a, b) => {
          const aIndex = ELECTRICS_ORDER.indexOf(a.electric)
          const bIndex = ELECTRICS_ORDER.indexOf(b.electric)

          if (aIndex === -1 && bIndex === -1) return 0
          if (aIndex === -1) return 1
          if (bIndex === -1) return -1

          return aIndex - bIndex
        })
        .map((electric) => ({
          ...electric,
          amounts: electric.amounts.sort((a, b) => a - b),
        })),
    })) as Array<{
      duration: validDurations
      electrics: Array<{
        electric: validElectric
        amounts: number[]
      }>
    }>
  }, [availableConfigs])

  return {
    sites,
    headers,
    prices,
    isLoading,
    priceConfig,
    sitePriceCounts,
    updatePriceConfig,
    removePriceConfig,
    enablePrice,
    disablePrice,
    selectAll,
    unselectAll,
  }
}

export default usePricingTable
